import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  assignProviderReviewTask,
  getProviderApiConfig,
  getProviderReviewQueue,
  routeProviderCandidates,
} from '../../api/providerApi';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { NoticeBanner } from '../../components/ui/NoticeBanner';
import { DataTable } from '../../components/ui/DataTable';
import { FilterSelect } from '../../components/ui/FilterSelect';
import { Pagination } from '../../components/ui/Pagination';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button, buttonBase, buttonVariants } from '../../components/ui/Button';
import {
  RECOMMENDATION_FILTER_OPTIONS,
  TASK_STATUS_OPTIONS,
  authorityStateLabel,
  canRouteToCustodian,
  candidateDetailPath,
  nextHandlerLabel,
  recommendationLabel,
  taskStatusLabel,
} from './providerReviewLabels';

export function AuthorityReviewQueuePage() {
  const providerUserId = getProviderApiConfig().userId;
  const [statusFilter, setStatusFilter] = useState('');
  const [recommendationFilter, setRecommendationFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionNotice, setActionNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadQueue() {
      setLoading(true);
      setError('');
      try {
        const response = await getProviderReviewQueue({
          page,
          pageSize,
          status: statusFilter || undefined,
          recommendation: recommendationFilter || undefined,
          assignedTo: ownerFilter === 'mine' ? providerUserId : undefined,
        });
        if (cancelled) return;
        const nextItems = response.items || [];
        setItems(nextItems);
        setTotal(response.meta?.total || 0);
        setSelectedAssetId((current) => {
          if (nextItems.some((row) => row.asset_id === current)) return current;
          return nextItems[0]?.asset_id ?? null;
        });
      } catch (requestError) {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setSelectedAssetId(null);
        setError(requestError.message || 'Unable to load the Provider review queue.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadQueue();
    return () => {
      cancelled = true;
    };
  }, [
    page,
    pageSize,
    statusFilter,
    recommendationFilter,
    ownerFilter,
    providerUserId,
    reloadToken,
  ]);

  const selected = items.find((row) => row.asset_id === selectedAssetId) ?? null;

  const refresh = () => setReloadToken((value) => value + 1);

  const handleRoute = async () => {
    if (!canRouteToCustodian(selected) || busy) return;
    setBusy(true);
    setActionError('');
    setActionNotice('');
    try {
      await routeProviderCandidates([selected.task_id]);
      setActionNotice('Routed to Custodian');
      refresh();
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to route this candidate.');
    } finally {
      setBusy(false);
    }
  };

  const handleAssignToMe = async () => {
    if (!selected?.task_id || selected.task_status !== 'UNASSIGNED' || busy) return;
    setBusy(true);
    setActionError('');
    setActionNotice('');
    try {
      await assignProviderReviewTask(selected.task_id, providerUserId);
      setActionNotice('Assigned to current Provider user.');
      refresh();
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to assign this review task.');
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'asset',
        header: 'Asset',
        render: (row) => (
          <Link
            to={candidateDetailPath(row.asset_id, row.task_id)}
            onClick={(event) => event.stopPropagation()}
            className="font-semibold text-eorig-navy-600 hover:text-eorig-cyan hover:underline"
          >
            {row.asset_number}
          </Link>
        ),
      },
      {
        key: 'batch',
        header: 'Package',
        render: (row) => (
          <span className="font-mono text-[11px] text-eorig-muted">{row.batch || '—'}</span>
        ),
      },
      {
        key: 'program',
        header: 'Program',
        render: (row) => row.program || '—',
      },
      {
        key: 'task_status',
        header: 'Task status',
        render: (row) => (
          <StatusPill value={row.task_status} label={taskStatusLabel(row.task_status)} />
        ),
      },
      {
        key: 'authority_state',
        header: 'Authority',
        render: (row) => (
          <StatusPill value={row.authority_state} label={authorityStateLabel(row.authority_state)} />
        ),
      },
      {
        key: 'recommendation',
        header: 'Provider rec.',
        render: (row) =>
          row.recommendation ? (
            <StatusPill
              value={row.recommendation}
              label={recommendationLabel(row.recommendation)}
            />
          ) : (
            <span className="text-eorig-muted">None</span>
          ),
      },
      {
        key: 'next',
        header: 'Next handler',
        render: (row) => (
          <span className="text-eorig-ink">{nextHandlerLabel(row)}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <section className="flex flex-wrap items-center gap-2 rounded-lg border border-[#E6EAF0] bg-white px-3 py-2">
        <FilterSelect
          label="Status"
          ariaLabel="Filter by task status"
          options={TASK_STATUS_OPTIONS}
          value={statusFilter}
          onChange={(value) => {
            setPage(1);
            setStatusFilter(value);
          }}
        />
        <FilterSelect
          label="Recommendation"
          ariaLabel="Filter by recommendation"
          options={RECOMMENDATION_FILTER_OPTIONS}
          value={recommendationFilter}
          onChange={(value) => {
            setPage(1);
            setRecommendationFilter(value);
          }}
        />
        <FilterSelect
          label="Queue"
          ariaLabel="Filter by assignee"
          options={[
            { value: 'all', label: 'All queues' },
            { value: 'mine', label: 'My queue' },
          ]}
          value={ownerFilter}
          onChange={(value) => {
            setPage(1);
            setOwnerFilter(value);
          }}
        />
      </section>

      {error ? <ErrorBanner message={error} onRetry={refresh} /> : null}
      {actionError ? <ErrorBanner message={actionError} /> : null}
      {actionNotice ? <NoticeBanner>{actionNotice}</NoticeBanner> : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_min(22rem,100%)] lg:items-start">
        <div className="min-w-0 space-y-3">
          <DataTable
            columns={columns}
            rows={items}
            rowKey={(row) => row.task_id}
            onRowClick={(row) => setSelectedAssetId(row.asset_id)}
            isSelected={(row) => row.asset_id === selectedAssetId}
            loading={loading}
            emptyMessage="No review tasks in this queue."
            minWidth={880}
          />
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={loading}
          />
        </div>

        <aside className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card sm:p-5">
          <h2 className="text-sm font-semibold text-eorig-ink">Selected candidate</h2>
          {selected ? (
            <>
              <p className="mt-3 text-sm font-medium text-eorig-ink">
                Asset {selected.asset_number} / Loan {selected.loan_number}
              </p>
              <ul className="mt-3 space-y-2 text-sm text-eorig-muted">
                <li>Package: {selected.batch || '—'}</li>
                <li>Program: {selected.program || '—'}</li>
                <li>Task: {taskStatusLabel(selected.task_status)}</li>
                <li>Recommendation: {recommendationLabel(selected.recommendation)}</li>
                <li>Authority state: {authorityStateLabel(selected.authority_state)}</li>
                <li>Next handler: {nextHandlerLabel(selected)}</li>
              </ul>

              <div className="mt-6 flex flex-col gap-2">
                <Button
                  type="button"
                  variant="navy"
                  onClick={handleRoute}
                  disabled={!canRouteToCustodian(selected) || busy}
                >
                  {busy ? 'Working…' : 'Route to custodian'}
                </Button>
                <Link
                  to={candidateDetailPath(selected.asset_id, selected.task_id)}
                  className={`${buttonBase} ${buttonVariants.gradient} w-full`}
                >
                  Open eligibility review
                </Link>
                <button
                  type="button"
                  className="text-xs font-semibold text-eorig-navy-600 hover:text-eorig-cyan disabled:pointer-events-none disabled:opacity-50"
                  onClick={handleAssignToMe}
                  disabled={selected.task_status !== 'UNASSIGNED' || busy}
                >
                  Assign to me
                </button>
              </div>
            </>
          ) : (
            <p className="mt-4 text-sm text-eorig-muted">
              Select a row to review candidate details and choose an action.
            </p>
          )}
        </aside>
      </div>

      <section className="rounded-2xl border border-eorig-border border-l-4 border-l-eorig-cyan bg-white p-4 shadow-eorig-card sm:p-5">
        <p className="text-xs leading-relaxed text-eorig-muted">
          Only the provider uses this queue to decide whether an asset is a candidate
          for Custodian review, should remain repository-only, be returned, or be
          blocked. Provider CANDIDATE is eligibility only — it is not legal authority
          approval and does not admit the asset to the vault.
        </p>
      </section>
    </div>
  );
}
